import React, { useState } from 'react';
import Isvg from 'react-inlinesvg';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Dropdown, DropdownItem, DropdownMenu } from 'components/Dropdown2';
import Message from 'components/Message';
import { SvgArrow } from 'components/Svgs';
import { TooltipContent, TooltipToggle } from 'components/Tooltip';
import { ICONS, PFE_ICONS } from 'constants/icons';
import { useCollapseState } from 'features/shared/useCollapseState';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import { selectLocale } from 'selectors/locale-data';
import { selectIsMobile } from 'selectors/ui';

import MobileActionSheet from 'components/MobileActionSheet';
import { skuCategoryGroupMessages, skuItemsGroupMessages, skuCategorySectionMessages } from '../SkuItems.messages';
import skuMenuPageMessages from '../SkuMenuPage.messages';
import useSkuVenuePermissions from '../useSkuVenuePermissions';

import SkuItemRow from './SkuItemRow';

const { block, element } = bem('sku-category-section');
const { element: itemsGroupElement } = bem('items-group');

const SkuCategorySection = ({
  category,
  currencyCode,
  collapseStorageKey,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onEditCategory,
  onDuplicateCategory,
  onToggleCategory,
  onDeleteCategory,
  onStatusChange,
  onViewItem,
  onViewCategory,
}) => {
  const intl = useIntl();
  const isMobile = useSelector(selectIsMobile);
  const isDisabled = category.enabled === false;
  const [isOpen, setIsOpen] = useCollapseState(collapseStorageKey, `cat-${category.id}`, !isDisabled);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const { canAddItem } = useSkuVenuePermissions();
  const locale = useSelector(selectLocale);
  const items = category.items || [];
  const panelId = `sku-category-panel-${category.id}`;
  const payoutTooltipName = `sku-payout-tooltip-${category.id}`;
  const disabledTooltipName = `sku-category-disabled-${category.id}`;

  return (
    <section className={block()}>
      <div className={element('header')}>
        {onViewCategory ? (
          <>
            <button
              type="button"
              className={element('header-toggle', 'arrow-only')}
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => setIsOpen(open => !open)}>
              <span className={element('arrow')}>
                <SvgArrow direction={isOpen ? 'down' : 'right'} theme="dark" />
              </span>
            </button>
            <button
              type="button"
              className={element('title', { disabled: isDisabled, clickable: true })}
              onClick={() => onViewCategory(category)}>
              {category.name}
            </button>
            {isDisabled && (
              <TooltipToggle
                name={disabledTooltipName}
                className={element('disabled-indicator')}
                aria-label={intl.formatMessage(skuCategoryGroupMessages.disabledCategoryTooltip)}>
                <Isvg src={ICONS.eyeWithCross} />
              </TooltipToggle>
            )}
          </>
        ) : (
          <button
            type="button"
            className={element('header-toggle')}
            data-testid={`sku-category-toggle-${category.id}`}
            aria-expanded={isOpen}
            aria-controls={panelId}
            onClick={() => setIsOpen(open => !open)}>
            <span className={element('arrow')}>
              <SvgArrow direction={isOpen ? 'down' : 'right'} theme="dark" />
            </span>
            <div className={element('title', { disabled: isDisabled })}>{category.name}</div>
            {isDisabled && (
              <TooltipToggle
                name={disabledTooltipName}
                className={element('disabled-indicator')}
                aria-label={intl.formatMessage(skuCategoryGroupMessages.disabledCategoryTooltip)}>
                <Isvg src={ICONS.eyeWithCross} />
              </TooltipToggle>
            )}
          </button>
        )}
        {isDisabled && (
          <TooltipContent name={disabledTooltipName} place="bottom">
            <Message message={skuCategoryGroupMessages.disabledCategoryTooltip} />
          </TooltipContent>
        )}
        {isOpen && (
          <div className={element('columns')}>
            <div className={element('column-spacer')} />
            <div className={element('column-payout')}>
              <Message message={skuItemsGroupMessages.payout} />
              <TooltipToggle className={element('tooltip-toggle')} name={payoutTooltipName}>
                <Isvg src={ICONS.infoGreyLight} />
              </TooltipToggle>
              <TooltipContent
                hoverable
                name={payoutTooltipName}
                place="top"
                className={element('payout-tooltip-content')}>
                <Message
                  message={skuItemsGroupMessages.payoutTooltip}
                  values={{
                    a: text => (
                      <a href={makePath.partnerTaxDefinition(locale)} target="_blank" rel="noopener noreferrer">
                        {text}
                      </a>
                    ),
                  }}
                />
              </TooltipContent>
            </div>
            <Message as="div" className={element('column-availability')} message={skuItemsGroupMessages.availability} />
          </div>
        )}
        {!(onEditCategory || onDuplicateCategory || onToggleCategory || onDeleteCategory) && (
          <div className={element('context-menu-slot')} />
        )}
        {(onEditCategory || onDuplicateCategory || onToggleCategory || onDeleteCategory) && isMobile && (
          <div className={element('context-menu')}>
            <button
              type="button"
              className={element('context-menu-btn')}
              aria-label={intl.formatMessage(skuCategorySectionMessages.categoryOptionsAriaLabel, {
                categoryName: category.name,
              })}
              onClick={() => setIsSheetOpen(true)}>
              <Isvg src={PFE_ICONS.threeDots} />
            </button>
            <MobileActionSheet
              isOpen={isSheetOpen}
              onClose={() => setIsSheetOpen(false)}
              options={[
                [
                  onEditCategory && {
                    icon: ICONS.editPencilSku,
                    label: intl.formatMessage(skuCategorySectionMessages.editCategory),
                    onClick: () => onEditCategory(category),
                  },
                  onDuplicateCategory && {
                    icon: ICONS.copy2,
                    label: intl.formatMessage(skuCategorySectionMessages.duplicateCategory),
                    onClick: () => onDuplicateCategory(category),
                  },
                ].filter(Boolean),
                [
                  onToggleCategory && {
                    icon: isDisabled ? ICONS.eye : ICONS.eyeWithCrossDark,
                    label: intl.formatMessage(
                      isDisabled
                        ? skuCategorySectionMessages.enableCategory
                        : skuCategorySectionMessages.disableCategory
                    ),
                    onClick: () => onToggleCategory(category),
                  },
                  onDeleteCategory && {
                    icon: ICONS.trashCanBlack,
                    label: intl.formatMessage(skuCategorySectionMessages.deleteCategory),
                    onClick: () => onDeleteCategory(category),
                  },
                ].filter(Boolean),
              ].filter(group => group.length > 0)}
            />
          </div>
        )}
        {(onEditCategory || onDuplicateCategory || onToggleCategory || onDeleteCategory) && !isMobile && (
          <Dropdown attachment="right" className={element('context-menu')}>
            {({ toggleMenu }) => (
              <>
                <button
                  type="button"
                  className={element('context-menu-btn')}
                  aria-label={intl.formatMessage(skuCategorySectionMessages.categoryOptionsAriaLabel, {
                    categoryName: category.name,
                  })}
                  onClick={toggleMenu}>
                  <Isvg src={PFE_ICONS.threeDots} />
                </button>
                <DropdownMenu>
                  {onEditCategory && (
                    <DropdownItem onClick={() => onEditCategory(category)}>
                      <span className={element('dropdown-icon')}>
                        <Isvg src={ICONS.editPencilSku} />
                      </span>
                      <Message message={skuCategorySectionMessages.editCategory} />
                    </DropdownItem>
                  )}
                  {onDuplicateCategory && (
                    <DropdownItem onClick={() => onDuplicateCategory(category)}>
                      <span className={element('dropdown-icon')}>
                        <Isvg src={ICONS.copy2} />
                      </span>
                      <Message message={skuCategorySectionMessages.duplicateCategory} />
                    </DropdownItem>
                  )}
                  {onToggleCategory && (
                    <DropdownItem onClick={() => onToggleCategory(category)}>
                      <span className={element('dropdown-icon')}>
                        <Isvg src={isDisabled ? ICONS.eye : ICONS.eyeWithCrossDark} />
                      </span>
                      <Message
                        message={
                          isDisabled
                            ? skuCategorySectionMessages.enableCategory
                            : skuCategorySectionMessages.disableCategory
                        }
                      />
                    </DropdownItem>
                  )}
                  {onDeleteCategory && (
                    <DropdownItem onClick={() => onDeleteCategory(category)}>
                      <span className={element('dropdown-icon')}>
                        <Isvg src={ICONS.trashCanBlack} />
                      </span>
                      <Message message={skuCategorySectionMessages.deleteCategory} />
                    </DropdownItem>
                  )}
                </DropdownMenu>
              </>
            )}
          </Dropdown>
        )}
      </div>
      <div id={panelId} hidden={!isMobile && !isOpen} aria-hidden={!isMobile && !isOpen}>
        {items.length > 0 && (
          <div className={element('items')}>
            {items.map(item => (
              <SkuItemRow
                key={item.id}
                item={item}
                currencyCode={currencyCode}
                onEditItem={onEditItem && (selectedItem => onEditItem(selectedItem, category))}
                onDeleteItem={onDeleteItem && (selectedItem => onDeleteItem(selectedItem, category))}
                onStatusChange={onStatusChange}
                onViewItem={onViewItem && (selectedItem => onViewItem(selectedItem, category))}
              />
            ))}
          </div>
        )}
        {items.length === 0 && !canAddItem && (
          <Message as="p" className={element('empty')} message={skuCategorySectionMessages.emptyCategory} />
        )}
        {canAddItem && onAddItem && (
          <div className={`${element('add-item-row')} ${itemsGroupElement('add-item-row')}`}>
            <button
              type="button"
              className={`${element('add-item-btn')} ${itemsGroupElement('add-item-btn')}`}
              aria-label={intl.formatMessage(skuMenuPageMessages.addItem)}
              onClick={() => onAddItem(category)}>
              <Isvg src={ICONS.addGrey} />
              <Message as="span" message={skuMenuPageMessages.addItem} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

SkuCategorySection.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    enabled: PropTypes.bool,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
  currencyCode: PropTypes.string,
  collapseStorageKey: PropTypes.string.isRequired,
  onAddItem: PropTypes.func,
  onEditItem: PropTypes.func,
  onDeleteItem: PropTypes.func,
  onEditCategory: PropTypes.func,
  onDuplicateCategory: PropTypes.func,
  onToggleCategory: PropTypes.func,
  onDeleteCategory: PropTypes.func,
  onStatusChange: PropTypes.func,
  onViewItem: PropTypes.func,
  onViewCategory: PropTypes.func,
};

export default SkuCategorySection;
